import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type Edge,
  type NodeTypes,
} from '@xyflow/react';
import { Maximize2, Minimize2 } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { OrgNodeCard } from './OrgNodeCard';
import { layoutWithDagre } from './useDagreLayout';
import {
  collectAllParentIds,
  collectAncestorIds,
  collectMatchIds,
  treeToGraph,
} from './org-graph';
import type { OrgChartProps, OrgFlowNode } from './types';

// Stable references — a new object here would force React Flow to remount every node.
const nodeTypes: NodeTypes = { orgNode: OrgNodeCard };
const defaultEdgeOptions = { type: 'smoothstep', style: { stroke: 'hsl(var(--border))' } };
const EMPTY_SET: ReadonlySet<string> = new Set();

function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
}

function OrgChartInner({ tree, selectedId, onSelect, search, onSearchChange }: OrgChartProps) {
  const { resolvedTheme } = useTheme();
  const isDesktop = useIsDesktop();
  const { fitView } = useReactFlow();

  const [collapsedIds, setCollapsedIds] = useState<ReadonlySet<string>>(EMPTY_SET);

  const onToggleCollapse = useCallback((id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const searchActive = search.trim().length > 0;
  const matchedIds = useMemo(() => collectMatchIds(tree, search), [tree, search]);

  // While searching, reveal the ancestors of matches without losing the user's
  // manual collapse state (restored as soon as the search is cleared).
  const effectiveCollapsed = useMemo<ReadonlySet<string>>(() => {
    if (!searchActive) return collapsedIds;
    const reveal = collectAncestorIds(tree, matchedIds);
    const next = new Set(collapsedIds);
    for (const id of reveal) next.delete(id);
    return next;
  }, [collapsedIds, matchedIds, searchActive, tree]);

  // Geometry only depends on the tree shape + which subtrees are collapsed, so we
  // key the dagre pass on a string signature to avoid re-laying-out on selection.
  const collapsedKey = useMemo(() => [...effectiveCollapsed].sort().join(','), [effectiveCollapsed]);

  const layouted = useMemo(() => {
    const { nodes, edges } = treeToGraph(tree, {
      collapsedIds: effectiveCollapsed as Set<string>,
      selectedId,
      matchedIds,
      searchActive,
      onSelect,
      onToggleCollapse,
    });
    return { nodes: layoutWithDagre(nodes, edges), edges };
    // collapsedKey stands in for effectiveCollapsed (stable string identity).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, collapsedKey, onSelect, onToggleCollapse]);

  // Re-decorate nodes with volatile view state (selection / search) without
  // re-running the layout.
  const nodes = useMemo<OrgFlowNode[]>(
    () =>
      layouted.nodes.map((n) => ({
        ...n,
        data: {
          ...n.data,
          isSelected: selectedId === n.id,
          isMatch: matchedIds.has(n.id),
          searchActive,
        },
      })),
    [layouted.nodes, selectedId, matchedIds, searchActive],
  );
  const edges: Edge[] = layouted.edges;

  const expandAll = useCallback(() => {
    setCollapsedIds(EMPTY_SET);
    requestAnimationFrame(() => fitView({ duration: 300, padding: 0.2 }));
  }, [fitView]);

  const collapseAll = useCallback(() => {
    setCollapsedIds(collectAllParentIds(tree));
    requestAnimationFrame(() => fitView({ duration: 300, padding: 0.2 }));
  }, [fitView, tree]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      defaultEdgeOptions={defaultEdgeOptions}
      colorMode={resolvedTheme}
      fitView
      fitViewOptions={{ padding: 0.2 }}
      minZoom={0.2}
      maxZoom={1.5}
      nodesDraggable={false}
      nodesConnectable={false}
      onlyRenderVisibleElements
      proOptions={{ hideAttribution: true }}
    >
      <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="hsl(var(--border))" />
      <Controls showInteractive={false} />
      {isDesktop && (
        <MiniMap
          pannable
          zoomable
          nodeColor={() => 'hsl(var(--primary))'}
          className="!bg-card"
        />
      )}
      <Panel position="top-left" className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={search}
          onChange={onSearchChange}
          placeholder="Search…"
          containerClassName="w-48"
        />
        <Button variant="outline" size="sm" onClick={expandAll}>
          <Maximize2 className="h-3.5 w-3.5" /> Expand all
        </Button>
        <Button variant="outline" size="sm" onClick={collapseAll}>
          <Minimize2 className="h-3.5 w-3.5" /> Collapse all
        </Button>
      </Panel>
    </ReactFlow>
  );
}

export function OrgChart(props: OrgChartProps) {
  return (
    <ReactFlowProvider>
      <OrgChartInner {...props} />
    </ReactFlowProvider>
  );
}
